/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CustomTooltip } from '@opengeoweb/shared';
import { uiSelectors, uiTypes, uiActions } from '@opengeoweb/store';
import { Location } from '@opengeoweb/theme';
import { timeSeriesActions } from '../../../store';
import {
  getCurrentLocation,
  getIsMapPinDisabled,
} from '../../../store/selectors';
import { TimeSeriesActionOrigin } from '../../../store/types';
import type { TimeSeriesRootState } from '../../../store/types';
import { useTimeseriesTranslation } from '../../../utils/i18n';
import {
  TIME_SERIES_CONTAINER_BREAKPOINT_PX,
  TIME_SERIES_CONTAINER_NAME,
} from '../constants';
import { TimeSeriesTopRowButton } from '../TimeSeriesTopRowButton';

interface LocationSelectButtonConnectProps {
  panelId: string;
}

export const LocationSelectButtonConnect: React.FC<
  LocationSelectButtonConnectProps
> = ({ panelId }: LocationSelectButtonConnectProps) => {
  const dispatch = useDispatch();
  const { t } = useTimeseriesTranslation();
  const timeSeriesLocation = useSelector((state: TimeSeriesRootState) =>
    getCurrentLocation(state, panelId),
  );
  const currentSourcePanelId = useSelector((store: TimeSeriesRootState) =>
    uiSelectors.getSourcePanelId(
      store,
      uiTypes.DialogTypes.TimeseriesLocations,
    ),
  );
  const isLocationsDialogOpen = useSelector((store: TimeSeriesRootState) =>
    uiSelectors.getisDialogOpen(store, uiTypes.DialogTypes.TimeseriesLocations),
  );
  const isMapPinDisabled = useSelector((state: TimeSeriesRootState) =>
    getIsMapPinDisabled(state, panelId),
  );
  const isLocationsDialogOpenRef = React.useRef(isLocationsDialogOpen);
  const currentSourcePanelIdRef = React.useRef(currentSourcePanelId);

  React.useEffect(() => {
    isLocationsDialogOpenRef.current = isLocationsDialogOpen;
    currentSourcePanelIdRef.current = currentSourcePanelId;
  }, [isLocationsDialogOpen, currentSourcePanelId]);

  const selectedLocationName =
    typeof timeSeriesLocation?.occurrences?.[0]?.properties?.name === 'string'
      ? timeSeriesLocation.occurrences[0].properties.name
      : '';

  const toggleLocationsDialog = (): void => {
    const newIsOpen =
      currentSourcePanelId !== panelId ? true : !isLocationsDialogOpen;
    dispatch(
      uiActions.setToggleOpenDialog({
        type: uiTypes.DialogTypes.TimeseriesLocations,
        setOpen: newIsOpen,
        sourcePanelId: panelId,
      }),
    );
    dispatch(
      timeSeriesActions.setLocationsDialogOpen({
        panelId,
        isLocationsDialogOpen: newIsOpen,
        origin: TimeSeriesActionOrigin.timeSeriesManager,
      }),
    );
  };

  React.useEffect(
    () => (): void => {
      if (
        isLocationsDialogOpenRef.current &&
        currentSourcePanelIdRef.current === panelId
      ) {
        dispatch(
          uiActions.setToggleOpenDialog({
            setOpen: false,
            type: uiTypes.DialogTypes.TimeseriesLocations,
            sourcePanelId: panelId,
          }),
        );
      }
    },
    [dispatch, panelId],
  );

  return (
    <CustomTooltip
      title={selectedLocationName || t('timeseries-select-location')}
    >
      <span>
        <TimeSeriesTopRowButton
          aria-label={t('timeseries-location')}
          data-testid="locationSelectButton"
          disabled={isMapPinDisabled}
          icon={
            <Location
              sx={{
                [`@container ${TIME_SERIES_CONTAINER_NAME} (width < ${TIME_SERIES_CONTAINER_BREAKPOINT_PX})`]:
                  {
                    display: 'none',
                  },
              }}
            />
          }
          longText={selectedLocationName || t('timeseries-location')}
          onClick={toggleLocationsDialog}
          selected={
            isLocationsDialogOpen ? currentSourcePanelId === panelId : null!
          }
          shortText={selectedLocationName || t('timeseries-location')}
        />
      </span>
    </CustomTooltip>
  );
};
